import { Unit, Weapon } from "@/services/calc/Calculator";
import { GameSystems } from "./gameSystems";

export type RuleId = string;

export interface ISpecialRule {
  id?: RuleId;
  name: string;
  rating?: string | number;
  description?: string;
  descriptionOverride?: any;
  hideModifier?: boolean;
}

export interface IDataSpecialRule {
  id: string;
  name: string;
  description: string;
  descriptionOverride?: {
    skirmish?: string;
    regiments?: string;
  };
  hasRating: boolean;
  defaultRating?: number;
  costFormula: RuleCostFormula[];
  enabledGameSystems: number[];
  armyBooks?: { uid: string; name: string; enabledGameSystems: GameSystems[] }[];
  aliases?: { id: string; name: string }[];
  formulaReferences?: { id: string; name: string }[];
  genReferences?: { id: string; name: string }[];
  aliasedRuleId?: string;
  ratingValues?: string;
  woundFormula?: string;
  rangedWoundFormula?: string;
  custom?: boolean;
  ruleType: SpecialRuleType;
  targetType: RuleTargetType;
  coreType: RuleCoreType;
  category?: RuleCategory;
  params?: any;
  variants?: string[];
  weaponWeight?: number;
  archived?: boolean;
  ruleGenEnabled?: boolean;
  generation?: RuleGenerationSelections;
  replaceWith?: string;
  weight?: string;
  tags?: string[];
  isInherited?: boolean;
  originalName?: string;
}

export interface RuleCostFormula {
  type: string;
  value: any;
  flags?: RuleCostFlags;
}

export enum RuleTargetType {
  Unit = 1,
  Weapon = 2
}

export enum RuleCoreType {
  Core = 1,
  //Base = 2,
  Calc = 3,
  //Generated = 4,
  Common = 5
}

export enum RuleCategory {
  Offensive = 1,
  Defensive = 2,
  Utility = 3
}

export enum SpecialRuleType {
  Default = 0,
  QuestConsumable = 1,
  QuestInjury = 2,
  CampaignTrait = 3,
  QuestCondition = 4,
  QuestClass = 7
}

export type CalcRule = {
  name: string;
  formula: RuleFormula[];
};

export enum RuleCostFlags {
  //None = 0,
  OnSix = 1 << 0,
  SixMultiplier = 1 << 1,
  OnCharge = 1 << 2,
  OnStrikeBack = 1 << 3
}
export type ApplyRuleCost = {
  name: string;
  multiplier?: number;
  rating?: any;
  unique?: boolean;
  avgUnit: boolean;
  meleeOnly?: boolean;
  rangedOnly?: boolean;
};

// Define the base type with a common property
interface BaseRuleFormula {
  value: string; // Common property for all rule formulas
  flags: RuleCostFlags;
}

export type RuleFormula =
  | (BaseRuleFormula & {
      type: "cost";
      fn: ((unit: Unit) => number) | ((weapon: Weapon, unit: Unit) => number);
    })
  | (BaseRuleFormula & { type: "unitCost"; fn: (unit: Unit) => number })
  | (BaseRuleFormula & { type: "modifyUnit"; fn: (unit: Unit, weapon: Weapon) => void })
  | (BaseRuleFormula & { type: "addRules"; fn: () => string[] })
  | (BaseRuleFormula & {
      type: "applyRuleCost";
      fn: ((unit: Unit, baseUnit: Unit) => ApplyRuleCost) | ((weapon: Weapon, unit: Unit) => ApplyRuleCost);
    })
  | (BaseRuleFormula & {
      type: "applyRuleCostWeapon";
      fn: (weapon: Weapon, unit: Unit) => ApplyRuleCost;
    })
  | (BaseRuleFormula & {
      type: "finalCostMultiplier";
      fn: (weapon: Weapon, unit: Unit) => number;
    })
  | (BaseRuleFormula & { type: "properties"; fn: (unit: Unit) => void })
  | (BaseRuleFormula & { type: "weaponProperties"; fn: (weapon: Weapon) => void })
  | (BaseRuleFormula & {
      type: "baseCostMultiplier";
      fn: ((unit: Unit) => number) | ((weapon: Weapon) => number);
    })
  | (BaseRuleFormula & { type: "unitPropertiesForWeapons"; fn: (unit: Unit, weapon: Weapon) => void })
  | (BaseRuleFormula & { type: "weaponBaseCostMultiplier"; fn: (weapon: Weapon) => number })
  | (BaseRuleFormula & { type: "modifyWeapons"; fn: (weapon: Weapon) => void })
  | (BaseRuleFormula & { type: "addQualityCost"; fn: (weapon: Weapon) => number })
  | (BaseRuleFormula & { type: "newQuality"; fn: (quality: number, weapon: Weapon) => number })
  | (BaseRuleFormula & { type: "weaponCost"; fn: (weapon: Weapon, unit: Unit) => number })
  | (BaseRuleFormula & { type: "weaponBaseCost"; fn: (weapon: Weapon) => number })
  | (BaseRuleFormula & {
      type: "initialise";
      fn: ((unit: Unit) => void) | ((weapon: Weapon, unit: Unit) => void);
    });

export type RuleFormulaType = RuleFormula["type"];

export type RuleGenFormulaParams = {
  [section in "trigger" | "assignment" | "effect"]?: {
    [key: string]: any;
  };
} & {
  multiplier: number;
  thisUnit: boolean;
  avgUnit: boolean;
  /**
   * @deprecated
   */
  aura: boolean; // <= v3.5.2 backwards compat
  thisUnitAura: boolean;
  otherUnitAura: boolean;
  /**
   * @deprecated
   */
  triggerOnSixOrOne?: boolean;
  triggerOnSix?: boolean;
  triggerOnOneBlock?: boolean;
  threshold?: number;
};

export enum RuleGenerationSectionType {
  Trigger = 1,
  Assignment = 2,
  Effect = 3
}

export interface RuleGenerationParameter {
  id: string;
  type?: RuleGenerationParameterType;
  name: string;
  values?: RuleGenerationParameterValue[];
  parent?: string;
  optional?: boolean;
}

export interface RuleGenGlobalParam {
  id: string;
  name: string;
  values: RuleGenerationParameterValue[];
}

export enum RuleGenerationParameterType {
  Global = -1,
  Custom = 0,
  NumberDigit = 1,
  Target = 2,
  AnyRule = 11,
  UnitRule = 12,
  WeaponRule = 13,
  AnyCoreRule = 14
}

export interface RuleGenerationParameterValue {
  key: string;
  value?: number | string;
  multiplier: number | string;
}

export interface RuleGenerationSection {
  id: string;
  type: RuleGenerationSectionType;
  name: string;
  template: string;
  multiplier: number;
  targetType: RuleTargetType;
  parameters: RuleGenerationParameter[];
  occurOnce?: boolean;
  takeMorale?: boolean;
  archived: boolean;
  costFormula?: RuleCostFormula[];
}

export interface RuleGenerationSelections {
  trigger: RuleGenerationSelectionSection;
  assignment: RuleGenerationSelectionSection;
  effect: RuleGenerationSelectionSection;
  hideModifier?: boolean;
}

export interface RuleGenerationSelectionSection {
  id: string;
  parameters: {
    name: string;
    value: string | RuleGenParamValue;
  }[];
}

export interface RuleGenParamValue {
  id: string;
  name: string;
  rating?: any;
  hideModifier?: boolean;
}

export interface RuleGenerationSentencePart {
  param?: string;
  text?: string;
  optional?: boolean;
  parts?: RuleGenerationSentencePart[];
}

export type RuleGenCategory = "Aura" | "Buff" | "Mark" | "Grant" | "Other";

export type RuleUsage = { id: string; name: string; rules: RuleId[] };
export type RuleWithUsage = {
  id: string;
  name: string;
  usage: {
    aliases?: { id: string; name: string }[];
    genReferences?: { id: string; name: string }[];
    formulaReferences?: { id: string; name: string }[];
    books?: { uid: string; name: string; enabledGameSystems: GameSystems[] }[];
    units?: { id: string; name: string; book: string }[];
    packages?: { id: string; name: string; book: string }[];
    spells?: { id: string; name: string; book: string }[];
  };
};

export type OfficialBookRulesResponse = {
  uid: string;
  name: string;
  enabledGameSystems: GameSystems[];
  units: RuleUsage[];
  upgradePackages: RuleUsage[];
  spells: RuleUsage[];
};
